import {useEffect, useMemo, useState} from 'react';
import type {FocusEvent} from 'react';

import {CheckmarkButton} from '../../button/checkmark-button/checkmark-button';
import {CloseButton} from '../../button/close-button/close-button';
import {Stack} from '../../layout/stack/stack';
import {TextField} from '../../textfield/textfield';

type Props = {
    isEditing: boolean;
    setIsEditing: (isEditing: boolean) => void;
    numberFormattedWithCountryCode: string | null | undefined;
    extension: string | null | undefined;
    onChange: (number: string, extension: string) => Promise<any>;
    preventSaveOnBlur?: boolean;
    ignorePasswordAutoFill?: boolean;
};

export function PhoneRowEditState({
    isEditing,
    setIsEditing,
    numberFormattedWithCountryCode,
    extension,
    onChange,
    preventSaveOnBlur,
    ignorePasswordAutoFill,
}: Props) {
    const [numberWithCountryCode, setNumberWithCountryCode] = useState<string>(
        numberFormattedWithCountryCode || ''
    );
    const [internalExtension, setInternalExtension] = useState<string>(extension || '');

    useEffect(() => {
        setNumberWithCountryCode(numberFormattedWithCountryCode || '');
        setInternalExtension(extension || '');
    }, [numberFormattedWithCountryCode, extension]);

    const trimmedInitialNumber = useMemo(
        () => (numberFormattedWithCountryCode || '').trim(),
        [numberFormattedWithCountryCode]
    );
    const trimmedInitialExtension = useMemo(() => (extension || '').trim(), [extension]);

    const handleSave = () => {
        const trimmedNumberWithCountryCode = numberWithCountryCode.trim();
        const trimmedExtension = internalExtension.trim();

        if (
            trimmedNumberWithCountryCode !== trimmedInitialNumber ||
            trimmedExtension !== trimmedInitialExtension
        ) {
            onChange(trimmedNumberWithCountryCode, trimmedExtension);
        } else {
            setIsEditing(false);
        }
    };

    const handleEscape = () => {
        setNumberWithCountryCode(numberFormattedWithCountryCode || '');
        setInternalExtension(extension || '');
        setIsEditing(false);
    };

    const handleContainerBlur = (event: FocusEvent<HTMLElement>) => {
        const related: any = event.relatedTarget;
        if (related && event.currentTarget && event.currentTarget.contains(related)) {
            return;
        }
        handleSave();
    };

    if (!isEditing) {
        return null;
    }

    return (
        <div onBlur={preventSaveOnBlur ? undefined : handleContainerBlur}>
            <Stack spacing={2} isHorizontal={true}>
                <Stack spacing={2} isFullWidth={true}>
                    <TextField
                        type='text'
                        value={numberWithCountryCode}
                        onChange={setNumberWithCountryCode}
                        onEnter={handleSave}
                        onEscape={handleEscape}
                        placeholder='Phone number'
                        // @ts-expect-error
                        autoFocus={true}
                        size='small'
                        ignorePasswordAutoFill={ignorePasswordAutoFill}
                    />
                    <TextField
                        type='text'
                        value={internalExtension}
                        onChange={setInternalExtension}
                        onEnter={handleSave}
                        onEscape={handleEscape}
                        placeholder='Extension'
                        size='small'
                        ignorePasswordAutoFill={ignorePasswordAutoFill}
                    />
                </Stack>
                <Stack alignItems='center' spacing={2}>
                    <CheckmarkButton
                        className='no-padding'
                        onClick={(event) => {
                            event.stopPropagation();
                            handleSave();
                        }}
                        // @ts-expect-error
                        variant='dark'
                    />
                    <CloseButton
                        size={12}
                        variant='dark'
                        onPointerDown={(event) => {
                            event.stopPropagation();
                            handleEscape();
                        }}
                    />
                </Stack>
            </Stack>
        </div>
    );
}
